import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEnvironmentNavigation } from '../../hooks/useEnvironmentNavigation';
import { useMyProjects } from '../../hooks/useProject';
import { ProjectStatus, Project } from '../../models/Project';
import ProjectListTable from '../../components/project/ProjectListTable';

const MyProjects: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { navigateByEnvironment } = useEnvironmentNavigation();

  const getStatusFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get('status') || '';
  };

  // State for search and pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // State for search form (values in the input fields) - initialize from URL
  const [statusFilter, setStatusFilter] = useState<string>(getStatusFromUrl());
  const [nameFilter, setNameFilter] = useState<string>('');
  const [projectNumberFilter, setProjectNumberFilter] = useState<string>('');
  
  // State for applied search filters (sent to API) - initialize from URL  
  const [appliedStatusFilter, setAppliedStatusFilter] = useState<string>(getStatusFromUrl());
  const [appliedNameFilter, setAppliedNameFilter] = useState<string>('');
  const [appliedProjectNumberFilter, setAppliedProjectNumberFilter] = useState<string>('');
  
  // State for sorting
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<string>('asc');

  // Update filters when URL changes (from menu navigation)
  useEffect(() => {
    const urlStatus = getStatusFromUrl();
    setStatusFilter(urlStatus);
    setAppliedStatusFilter(urlStatus);
    setPage(1);
  }, [location.search]);

  // Hooks - using applied filters only
  const { data, isLoading, error } = useMyProjects(
    page, 
    pageSize, 
    appliedStatusFilter ?? undefined,
    appliedNameFilter ?? undefined,
    appliedProjectNumberFilter ?? undefined,
    undefined, // companyId not used for myProjects
    sortField ?? undefined,
    sortDirection
  );

  const handleCreateProject = () => {
    navigate('/projects/create');
  };

  const handleViewProject = (id: number) => {
    navigateByEnvironment(`/projects/${id}`);
  };

  const handleEditProject = (id: number) => {
    navigateByEnvironment(`/projects/${id}/edit`);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleStatusFilterChange = (newStatusFilter: string) => {
    setStatusFilter(newStatusFilter);
  };

  const handleNameFilterChange = (newNameFilter: string) => {
    setNameFilter(newNameFilter);
  };

  const handleProjectNumberFilterChange = (newProjectNumberFilter: string) => {
    setProjectNumberFilter(newProjectNumberFilter);
  };

  const handleSearch = () => {
    // Apply current filter values to trigger search
    setAppliedStatusFilter(statusFilter);
    setAppliedNameFilter(nameFilter);
    setAppliedProjectNumberFilter(projectNumberFilter);
    setPage(1);
  };

  const handleReset = () => {
    // Reset form filters
    setStatusFilter('');
    setNameFilter('');
    setProjectNumberFilter('');
    // Reset applied filters
    setAppliedStatusFilter('');
    setAppliedNameFilter('');
    setAppliedProjectNumberFilter('');
    // Reset pagination
    setPage(1);
  };

  const handleSortChange = (field: string, direction: string) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(1); // Reset to first page when sorting
  };

  // For clients, only Draft projects can be edited
  const canEditProject = (project: Project) => {
    return project.status === ProjectStatus.Draft;
  };

  const projects = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 0;

  return (
    <ProjectListTable
      projects={projects}
      totalCount={totalCount}
      totalPages={totalPages}
      isLoading={isLoading}
      error={error}
      canCreateProject={true}
      showFeePercentage={false} // Clients never see fee percentage
      showCompanyFilter={false} // Clients don't need company filter
      title={t('project.myProjects')}
      createButtonText={t('project.createProject')}
      onCreateProject={handleCreateProject}
      onViewProject={handleViewProject}
      onEditProject={handleEditProject}
      canEditProject={canEditProject}
      page={page}
      pageSize={pageSize}
      statusFilter={statusFilter}
      nameFilter={nameFilter}
      projectNumberFilter={projectNumberFilter}
      sortField={sortField}
      sortDirection={sortDirection}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      onStatusFilterChange={handleStatusFilterChange}
      onNameFilterChange={handleNameFilterChange}
      onProjectNumberFilterChange={handleProjectNumberFilterChange}
      onCompanyFilterChange={() => {}} // Not used for clients
      onSortChange={handleSortChange}
      onSearch={handleSearch}
      onReset={handleReset}
    />
  );
};

export default MyProjects;